import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FileDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cvGeneratorService, type CVTemplate } from '../services/cv-generator.service';

interface CVGeneratorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId: number;
  candidateName: string;
}

export function CVGeneratorDialog({
  open,
  onOpenChange,
  candidateId,
  candidateName,
}: CVGeneratorDialogProps) {
  const queryClient = useQueryClient();
  const [templates, setTemplates] = useState<CVTemplate[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charger les templates automatiquement a l'ouverture du dialog
  useEffect(() => {
    if (!open) return;

    const loadTemplates = async () => {
      setIsLoadingTemplates(true);
      setError(null);
      try {
        const result = await cvGeneratorService.getTemplates();
        setTemplates(result.templates);
        setCompanyName(result.companyName);
        if (result.templates.length === 1) {
          setSelectedTemplate(result.templates[0].name);
        } else {
          setSelectedTemplate('');
        }
      } catch {
        setTemplates([]);
        setError('Aucun template disponible pour votre societe');
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, [open]);

  const handleGenerate = async () => {
    if (!selectedTemplate) return;

    setIsGenerating(true);
    setError(null);

    try {
      const blob = await cvGeneratorService.generateCV(
        candidateId,
        selectedTemplate,
      );

      // Generer le nom du fichier
      const trigramme = candidateName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 3);
      const societeName = companyName.replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `${trigramme}_${societeName}.docx`;

      cvGeneratorService.downloadCV(blob, filename);

      // Invalider le cache pour rafraichir la liste des documents
      queryClient.invalidateQueries({ queryKey: ['candidates', 'detail', candidateId] });

      onOpenChange(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la generation du CV');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setTemplates([]);
    setCompanyName('');
    setSelectedTemplate('');
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="h-5 w-5" />
            Generer un CV
          </DialogTitle>
          <DialogDescription>
            Generer un CV au format Word pour {candidateName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* Template */}
          <div className="space-y-2">
            <Label>Template{companyName ? ` (${companyName})` : ''}</Label>
            {isLoadingTemplates ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement des templates...
              </div>
            ) : templates.length > 0 ? (
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Selectionner un template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.name} value={t.name}>
                      {t.name.replace('.docx', '')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-muted-foreground">
                Aucun template disponible pour votre societe
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={!selectedTemplate || isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generation...
                </>
              ) : (
                <>
                  <FileDown className="mr-2 h-4 w-4" />
                  Telecharger
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
